import { useEffect, useRef } from "react";
import NexusStructuredReply from "./NexusStructuredReply";
import NexusThinkingOrb from "./NexusThinkingOrb";
import { InsightAnswerBody } from "../../admin/assistantUi";

function NexusAvatar({ landing = false }) {
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl shadow-sm ${
        landing ? "nexus-msg-avatar--landing" : "bg-gradient-to-br from-[#004f73] to-[#0a7ea4] text-white"
      }`}
      aria-hidden
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z" />
      </svg>
    </div>
  );
}

function UserBubble({ content, landing = false }) {
  return (
    <div className="flex justify-end gap-2 pl-4 sm:pl-8">
      <div
        className={`max-w-[92%] sm:max-w-[78%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
          landing ? "nexus-user-bubble--landing text-white" : "bg-[#004f73] text-white"
        }`}
      >
        <p className="whitespace-pre-wrap">{content}</p>
      </div>
    </div>
  );
}

function ThinkingIndicator({ label = "NEXUS", landing = false }) {
  return (
    <div className="flex gap-3 items-center py-1" aria-busy="true" aria-label="NEXUS is thinking">
      <NexusThinkingOrb landing={landing} size="sm" />
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider nexus-thinking-label">{label}</p>
        <p className="text-xs nexus-thinking-sublabel mt-0.5">Synthesizing…</p>
      </div>
    </div>
  );
}

function AssistantBubble({
  message,
  label = "NEXUS",
  loading = false,
  landing = false,
  onNavigate,
  onConfirmReminders,
  reminderSendBusy = false,
  currentPath = "",
}) {
  const structured = message?.structured;
  const content = message?.content || "";

  if (loading) {
    return <ThinkingIndicator label={label} landing={landing} />;
  }

  return (
    <div className="flex gap-2.5 items-start">
      <NexusAvatar landing={landing} />
      <div
        className={`nexus-assistant-bubble flex-1 min-w-0 rounded-2xl rounded-bl-md border overflow-hidden shadow-sm ${
          landing ? "nexus-assistant-bubble--landing" : "nexus-assistant-bubble--dock border-slate-200/90 bg-white"
        }`}
      >
        <div className="px-3 py-2 border-b border-inherit flex items-center justify-between gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider nexus-assistant-label">
            {message?.meta?.workflowTitle
              ? `Workflow · ${message.meta.workflowTitle}`
              : message?.meta?.kind === "workflow_reply"
                ? "Workflow"
                : label}
          </span>
          {structured?.confidence ? (
            <span
              className={`text-[9px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0 ${
                structured.confidence === "high"
                  ? "bg-emerald-500/15 text-emerald-600"
                  : structured.confidence === "medium"
                    ? "bg-amber-500/15 text-amber-600"
                    : "bg-slate-500/10 text-slate-500"
              }`}
            >
              {structured.confidence}
            </span>
          ) : null}
        </div>
        <div className="px-4 py-3.5 text-sm leading-relaxed min-h-[2.5rem] nexus-assistant-body">
          {structured ? (
            <NexusStructuredReply
              structured={structured}
              fallbackText={content}
              relatedPages={message.relatedPages || []}
              dataSources={message.dataSources || []}
              onClarify={message.onClarify}
              onConfirmReminders={onConfirmReminders}
              reminderSendBusy={reminderSendBusy}
              onNavigate={onNavigate}
              currentPath={currentPath}
              variant={landing ? "landing" : "default"}
            />
          ) : (
            <InsightAnswerBody text={content} />
          )}
        </div>
      </div>
    </div>
  );
}

function messageText(m) {
  const content = (m?.content || "").trim();
  if (content) return content;
  const structured = m?.structured;
  if (structured && typeof structured === "object") {
    return (structured.summary || "").trim();
  }
  return "";
}

export function mapApiMessagesToThread(messages = []) {
  return (messages || [])
    .filter((m) => m && (m.role === "user" || m.role === "assistant"))
    .map((m, index) => {
      const text = messageText(m) || (m.content || "").trim();
      if (!text && m.role === "assistant" && !m.structured) return null;
      if (!text && m.role === "user") return null;
      return {
        id: `${m.at || "msg"}-${index}-${m.role}`,
        role: m.role,
        content: text || (m.content || ""),
        structured: m.structured || null,
        relatedPages: m.relatedPages || [],
        dataSources: m.dataSources || [],
      };
    })
    .filter(Boolean);
}

export default function NexusConversationThread({
  messages = [],
  loading = false,
  pendingUserMessage = "",
  label = "NEXUS",
  onClarify,
  onConfirmReminders,
  reminderSendBusy = false,
  onNavigate,
  currentPath = "",
  variant = "default",
  embedded = false,
}) {
  const bottomRef = useRef(null);
  const landing = variant === "landing";

  useEffect(() => {
    const bottom = bottomRef.current;
    if (!bottom) return;
    const pane = bottom.closest(".nexus-chat-scroll-pane");
    if (embedded && pane) {
      pane.scrollTo({ top: pane.scrollHeight, behavior: "smooth" });
      return;
    }
    bottom.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, loading, pendingUserMessage, embedded]);

  if (!embedded && !messages.length && !loading && !pendingUserMessage) return null;

  const rootClass = embedded
    ? "flex flex-col gap-4 nexus-thread-embedded"
    : "space-y-4 max-h-[min(52vh,480px)] overflow-y-auto pr-1 -mr-1 nexus-thread-scroll";

  return (
    <div className={rootClass}>
      {messages.map((msg) =>
        msg.role === "user" ? (
          <UserBubble key={msg.id} content={msg.content} landing={landing} />
        ) : (
          <AssistantBubble
            key={msg.id}
            message={{ ...msg, onClarify }}
            label={label}
            landing={landing}
            onNavigate={onNavigate}
            onConfirmReminders={onConfirmReminders}
            reminderSendBusy={reminderSendBusy}
            currentPath={currentPath}
          />
        )
      )}
      {pendingUserMessage ? <UserBubble content={pendingUserMessage} landing={landing} /> : null}
      {loading ? <ThinkingIndicator label={label} landing={landing} /> : null}
      <div ref={bottomRef} className="h-px shrink-0" aria-hidden />
    </div>
  );
}
